import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ROUTES } from './routes';
import { useAuth } from '../../hooks/useAuth';
import { UserCircle, LogOut, Menu, X, Calendar } from 'lucide-react';
import { BOOK_CALL_URL } from '../../constants/links';

const NAV_HEIGHT = 69;

const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAppsActive = location.pathname.startsWith('/apps');

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav
        style={{ height: NAV_HEIGHT }}
        className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-purple-900/95 to-blue-900/95 backdrop-blur-md border-b border-white/10 flex items-center"
      >
        <div className="w-full max-w-screen-xl mx-auto px-4 flex items-center h-full">

          {/* Logo */}
          <Link to={ROUTES.About} className="flex items-center gap-2 shrink-0">
            <img src="/images/logo-wht.svg" alt="Stray Apps icon" className="h-9 w-9" />
            <img src="/images/Stray-wht.svg" alt="Stray Apps" className="h-9 w-auto" />
          </Link>

          {/* Desktop nav links */}
          <div className="hidden lg:flex items-center gap-6 ml-8">
            <Link
              to={ROUTES.Services}
              className={`text-sm font-medium transition-colors ${
                location.pathname === ROUTES.Services ? 'text-white' : 'text-gray-300 hover:text-white'
              }`}
            >
              Services
            </Link>
            <Link
              to={ROUTES.RESOURCES}
              className={`text-sm font-medium transition-colors ${
                location.pathname.startsWith('/resources') ? 'text-white' : 'text-gray-300 hover:text-white'
              }`}
            >
              Resources
            </Link>
            <Link
              to={ROUTES.APPS}
              className={`text-sm font-medium transition-colors ${
                isAppsActive ? 'text-white' : 'text-gray-300 hover:text-white'
              }`}
            >
              Apps Explorer
            </Link>
          </div>

          {/* Desktop auth — right */}
          <div className="hidden lg:flex items-center gap-3 ml-auto">
            <a
              href={BOOK_CALL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium hover:from-purple-600 hover:to-pink-600 transition-colors no-underline"
            >
              <Calendar className="w-4 h-4" /> Book a Call
            </a>
            {user ? (
              <>
                <Link
                  to={ROUTES.PROFILE}
                  className={`flex items-center gap-1.5 text-sm font-medium transition-colors no-underline ${
                    location.pathname === ROUTES.PROFILE ? 'text-white' : 'text-gray-300 hover:text-white'
                  }`}
                >
                  <UserCircle className="w-4 h-4" />
                  {user.displayName || user.email?.split('@')[0] || 'Profile'}
                </Link>
                <button
                  onClick={logout}
                  className="flex items-center gap-1.5 text-sm text-red-400 hover:text-red-300 transition-colors bg-transparent border-none cursor-pointer"
                >
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </>
            ) : (
              <>
                <Link
                  to={ROUTES.LOGIN}
                  className="text-sm text-gray-300 hover:text-white transition-colors no-underline"
                >
                  Login
                </Link>
               
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(v => !v)}
            className="lg:hidden ml-auto p-2 rounded-lg text-gray-300 hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setMobileOpen(false)}
          />
          <div
            className="fixed left-0 right-0 z-50 lg:hidden bg-gradient-to-b from-purple-900/98 to-blue-900/98 backdrop-blur-md border-b border-white/10 shadow-xl"
            style={{ top: NAV_HEIGHT }}
          >
            <nav className="px-4 py-4 flex flex-col gap-1">
              <Link to={ROUTES.Services}  className="px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 no-underline transition-colors">Services</Link>
              <Link to={ROUTES.RESOURCES} className="px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 no-underline transition-colors">Resources</Link>
              <Link to={ROUTES.APPS}      className="px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 no-underline transition-colors">Apps</Link>

              <a
                href={BOOK_CALL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-1.5 mx-3 mt-2 px-3 py-2.5 rounded-lg text-sm bg-gradient-to-r from-purple-500 to-pink-500 text-white font-medium no-underline transition-colors"
              >
                <Calendar className="w-4 h-4" /> Book a Call
              </a>

              <div className="border-t border-white/10 my-1" />
              {user ? (
                <>
                  <Link
                    to={ROUTES.PROFILE}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/5 no-underline transition-colors"
                  >
                    <UserCircle className="w-4 h-4" /> Profile
                  </Link>
                  <button
                    onClick={logout}
                    className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-red-400 hover:text-red-300 hover:bg-white/5 transition-colors bg-transparent border-none cursor-pointer text-left"
                  >
                    <LogOut className="w-4 h-4" /> Logout
                  </button>
                </>
              ) : (
                <div className="flex gap-2 px-3 pt-1 pb-2">
                  <Link to={ROUTES.LOGIN}    className="flex-1 text-center py-2 rounded-lg border border-white/20 text-sm text-gray-300 hover:text-white hover:border-white/40 no-underline transition-colors">Login</Link>
                </div>
              )}
            </nav>
          </div>
        </>
      )}
    </>
  );
};

export default Navbar;
